import React, { useEffect, useRef, useState } from 'react';
import SideDrawerHeader from './components/SideDrawerHeader';
import defaultCloseIcon from './close.svg';
import styles from './SideDrawer.module.scss';
import clsx from 'clsx';
import { handleBackgroundScroll } from '../../../utils/utils';
import SideDrawerFooter from './components/SideDrawerFooter';
import SideDrawerMobileHeader from './components/SideDrawerMobileHeader';
import SideDrawerMobileLayout from './components/SideDrawerMobileLayout';
import greyLock from '../../../assets/grey-lock.svg';
import * as sideDrawerStack from './sideDrawerStack';

interface SideDrawerProps {
    children?: React.ReactNode;
    heading?: string | React.ReactNode;
    allowClose?: boolean;
    onClose?: Function;
    width?: string;
    dontClose?: boolean;
    showHeading?: boolean;
    style?: React.CSSProperties;
    iconStyle?: React.CSSProperties;
    headerActions?: React.ReactNode;
    /** Header geometry: default 72px tall / 24px padding; compact 64px / 20px 24px; tight 72px / 16px. */
    headerVariant?: 'default' | 'compact' | 'tight';
    /** Accessible name for the dialog when `heading` is a node rather than a string. */
    ariaLabel?: string;
    fullScreenOnMobile?: boolean;
    closeIcon?: React.ReactNode;
}

function SideDrawer({
    children,
    heading = 'Upload Data',
    allowClose = true,
    onClose,
    width,
    dontClose,
    showHeading,
    style,
    iconStyle,
    headerActions,
    headerVariant = 'default',
    ariaLabel,
    fullScreenOnMobile = false,
    closeIcon,
}: SideDrawerProps) {
    const TIMING_MS = 500;
    const [closing, setClosing] = useState(false);
    const drawerBodyRef = useRef<HTMLDivElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const instanceId = useRef(Symbol('SideDrawer')).current;

    const getFocusableElements = (): HTMLElement[] => {
        if (!drawerBodyRef.current) return [];
        return Array.from(
            drawerBodyRef.current.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
        );
    };

    const trapFocus = (e: KeyboardEvent) => {
        if (e.key !== 'Tab' || !sideDrawerStack.isTop(instanceId)) return;
        const focusable = getFocusableElements();
        if (focusable.length === 0) {
            e.preventDefault();
            drawerBodyRef.current?.focus();
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        // The container holds focus on mount and is not one of its own focusable descendants, so
        // it is the reverse-Tab boundary as much as `first` is — without it, Shift+Tab straight
        // after open lands on a control behind the modal.
        if (e.shiftKey) {
            if (document.activeElement === first || document.activeElement === drawerBodyRef.current) {
                e.preventDefault();
                last.focus();
            }
        } else if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    const closeDrawerOnEsc = (e) => {
        if (!sideDrawerStack.isTop(instanceId)) return;
        if (!dontClose && e.key === 'Escape') closeDrawer();
    };

    useEffect(() => {
        sideDrawerStack.push(instanceId);
        handleBackgroundScroll(false);
        previouslyFocusedRef.current = document.activeElement as HTMLElement;
        document.addEventListener('keydown', closeDrawerOnEsc);
        document.addEventListener('keydown', trapFocus);

        // Move focus into the drawer on open — the dialog container, never the first focusable
        // control: focusing a native <select> pops its option list open on mount.
        drawerBodyRef.current?.focus();

        return () => {
            sideDrawerStack.remove(instanceId);
            handleBackgroundScroll(true);
            document.removeEventListener('keydown', closeDrawerOnEsc);
            document.removeEventListener('keydown', trapFocus);
            previouslyFocusedRef.current?.focus();
        };
    }, []);

    const closeDrawer = () => {
        if (dontClose) {
            onClose?.();
            return;
        }
        setClosing(true);
        setTimeout(() => {
            onClose?.();
            setClosing(false);
        }, TIMING_MS - 1);
    };

    return (
        <div
            className={clsx(styles.DrawerBackdrop, {
                [styles.CloseDrawerBackdrop]: closing,
            })}
            onClick={closeDrawer}
            style={{ animationDuration: `${TIMING_MS}ms` }}
        >
            <div
                ref={drawerBodyRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel ?? (typeof heading === 'string' ? heading : 'Dialog')}
                tabIndex={-1}
                className={clsx(styles.DrawerBody, {
                    [styles.CloseDrawer]: closing,
                    [styles.CompactHeader]: headerVariant === 'compact',
                    [styles.TightHeader]: headerVariant === 'tight',
                    [styles.FullScreenOnMobile]: fullScreenOnMobile,
                })}
                onClick={(e) => e.stopPropagation()}
                style={{ ...style, width: width ?? '', animationDuration: `${TIMING_MS}ms` }}
            >
                {showHeading && <SideDrawerHeader>{heading}</SideDrawerHeader>}
                {(allowClose || headerActions) && (
                    <div className={styles.headerActionsContainer}>
                        {headerActions}
                        {allowClose && (
                            <button
                                type="button"
                                aria-label="Close"
                                className={styles.CloseButton}
                                onClick={closeDrawer}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', ...iconStyle }}
                            >
                                {closeIcon ?? <img src={defaultCloseIcon} alt="" height="15px" />}
                            </button>
                        )}
                    </div>
                )}
                {children}
            </div>
        </div>
    );
}

SideDrawer.Header = SideDrawerHeader;
SideDrawer.Footer = SideDrawerFooter;
SideDrawer.MobileHeader = SideDrawerMobileHeader;
SideDrawer.MobileLayout = SideDrawerMobileLayout;

export default SideDrawer;
